import React, { useEffect } from 'react';
import { Box, Typography, Button, Grid, Container, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import ParticlesBackground from '../components/ParticlesBackground';
import { keyframes } from '@emotion/react';
import dashboardPreview from '../assets/dashboard-preview.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = `${fadeIn} 0.6s ease-out forwards`;
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      <ParticlesBackground />
      
      {/* Hero Section */}
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        pt: '100px',
        px: 2,
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6} className="animate-on-scroll">
              <Typography variant={isMobile ? 'h4' : 'h2'} component="h1" sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                mb: 3,
                color: theme.palette.common.white,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                '& span': {
                  background: `linear-gradient(45deg, ${theme.palette.common.white}, ${theme.palette.secondary.light})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }
              }}>
                Transform Your Data<br />
                <span>Into Strategic Insights</span>
              </Typography>

              <Typography variant="h6" sx={{
                mb: 4,
                color: theme.palette.common.white,
                fontWeight: 500,
                fontSize: '1.1rem',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
              }}>
                AI-powered analytics platform designed for modern business intelligence
              </Typography>

              <Button
                component={Link}
                to="/auth"
                variant="contained"
                size="large"
                sx={{
                  background: `linear-gradient(45deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                  color: theme.palette.common.white,
                  fontSize: '1.1rem',
                  px: 5,
                  py: 1.5,
                  borderRadius: '12px',
                  boxShadow: theme.shadows[4],
                  textTransform: 'none',
                  fontWeight: 600,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[6]
                  }
                }}
              >
                Start Free Trial
              </Button>
            </Grid>

            <Grid item xs={12} md={6} className="animate-on-scroll">
              <Box sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 1920,
                mx: 'auto',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -40,
                  left: '5%',
                  right: '5%',
                  height: 40,
                  // background: theme.palette.grey[300], // Adjust or remove this if not needed
                  borderRadius: '0 0 8px 8px'
                }
              }}>
                {/* Screen Container */}
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: 0,
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  backgroundColor: 'transparent', // Ensure transparency
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: theme.shadows[10]
                }}>
                  {/* Browser Chrome */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 48,
                    background: theme.palette.grey[300], // Adjust or remove this if not needed
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    zIndex: 2
                  }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: '#ff5f56',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.2)' } 
                      }} />
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: '#ffbd2e',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.2)' } 
                      }} />
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: '#27c93f',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'scale(1.2)' } 
                      }} />
                    </Box>
                  </Box>

                  {/* Dashboard Image */}
                  <Box sx={{
                    position: 'absolute',
                    top: 48,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${dashboardPreview})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center center',
                    backgroundRepeat: 'no-repeat',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(195deg, rgba(25,25,25,0.1) 0%, rgba(25,25,25,0.02) 100%)`,
                      zIndex: 1
                    },
                    transform: 'translateZ(0)',
                    transition: 'transform 0.3s ease-out',
                    '&:hover': {
                      transform: 'translateZ(10px)'
                    }
                  }} />
                </Box>

                {/* Laptop Base */}
                <Box sx={{
                  position: 'absolute',
                  bottom: -60,
                  left: '10%',
                  right: '10%',
                  height: 60,
                }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ 
        py: 10,
        background: theme.palette.background.default
      }}>
        <Container maxWidth="xl">
          <Typography variant="h3" sx={{ 
            textAlign: 'center',
            mb: 6,
            fontWeight: 700,
            color: theme.palette.text.primary,
            animation: `${fadeIn} 0.6s ease-out`
          }}>
            Powerful Features
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {[
              { 
                title: 'Real-time Analytics', 
                icon: '⚡', 
                desc: 'Instant insights with sub-second response times' 
              },
              { 
                title: 'AI Predictions', 
                icon: '🤖', 
                desc: 'Machine learning-powered forecasting' 
              },
              { 
                title: 'Collaboration', 
                icon: '👥', 
                desc: 'Team-based workflow management' 
              },
            ].map((feature, index) => (
              <Grid item xs={12} md={4} key={index} className="animate-on-scroll">
                <Box sx={{
                  p: 4,
                  borderRadius: '16px',
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: '100%',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[4]
                  }
                }}>
                  <Typography variant="h2" sx={{ 
                    mb: 2,
                    fontSize: '3.5rem',
                    lineHeight: 1,
                    color: theme.palette.primary.main
                  }}>
                    {feature.icon}
                  </Typography>
                  <Typography variant="h5" sx={{ 
                    mb: 2,
                    fontWeight: 700,
                    color: theme.palette.text.primary
                  }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: theme.palette.text.secondary,
                    lineHeight: 1.6
                  }}>
                    {feature.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;